import { useState, useEffect } from 'react'
import { BreathingControls } from '@/components/BreathingControls'
import { BreathingCanvas } from '@/components/BreathingCanvas'
import { useBreathingSession } from '@/hooks/useBreathingSession'
import { BreathingConfig } from '@/types/breathing'

function App() {
  const [config, setConfig] = useState<BreathingConfig>({
    duration: 5,
    inhaleTime: 4,
    exhaleTime: 6,
    isRunning: false
  })

  const { sessionData, breathingState, startSession, stopSession } = useBreathingSession(config)

  const handleConfigChange = (newConfig: BreathingConfig) => {
    if (!sessionData.isActive) {
      setConfig(newConfig)
    }
  }

  const handleStart = () => {
    setConfig(prev => ({ ...prev, isRunning: true }))
    startSession()
  }

  const handleStop = () => {
    setConfig(prev => ({ ...prev, isRunning: false }))
    stopSession()
    // Exit fullscreen
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.warn)
    }
  }

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && sessionData.isActive) {
        handleStop()
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [sessionData.isActive])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sessionData.isActive) {
        handleStop()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [sessionData.isActive])

  return (
    <div className="relative">
      <BreathingCanvas
        breathingState={breathingState}
        isActive={sessionData.isActive}
      />
      <BreathingControls
        config={config}
        sessionData={sessionData}
        breathingState={breathingState}
        onConfigChange={handleConfigChange}
        onStart={handleStart}
        onStop={handleStop}
      />
    </div>
  )
}

export default App
